import React, { useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_SCOPE } from '../../authConfig';
import { useTranslation } from '../../hooks/useTranslation';
import { isMobileDevice } from '../../utils/deviceDetect';
import { isAppRoute } from '../../utils/postLoginRedirect';

// The only B2C policy — Vipps Login is the sole identity provider behind it.
const AUTHORITY =
  'https://coordnorway.b2clogin.com/coordnorway.onmicrosoft.com/B2C_1_SignInSignUp';

const AuthPopup = ({ onClose }) => {
  const { instance, accounts } = useMsal();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      if (
        event.eventType === 'msal:loginSuccess' ||
        (event.eventType === 'msal:acquireTokenSuccess' && accounts.length > 0)
      ) {
        // Explicitly set the active account after login
        if (event.payload && event.payload.account) {
          instance.setActiveAccount(event.payload.account);
        } else if (accounts.length > 0) {
          // Fallback if payload doesn't have the account
          instance.setActiveAccount(accounts[0]);
        }

        // After a fresh login, hand routing back to App.js ('/' resolves to
        // dashboard or onboarding). Only from public pages — on app routes
        // the routing (incl. the needsWizard redirect) already takes over,
        // and navigating again could fight it.
        if (
          event.eventType === 'msal:loginSuccess' &&
          !isAppRoute(location.pathname)
        ) {
          navigate('/', { replace: true });
        }

        onClose(); // Close the popup on successful login or token acquisition
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance, accounts, onClose, navigate, location.pathname]);

  const handleLogin = () => {
    const loginRequest = {
      authority: AUTHORITY,
      scopes: ['openid', 'offline_access', API_SCOPE]
    };

    if (isMobileDevice()) {
      // Mobile: full-page redirect. Response is handled in index.js
      // via handleRedirectPromise after the browser returns.
      instance.loginRedirect(loginRequest).catch((error) => {
        console.error('Login redirect error:', error);
      });
    } else {
      instance.loginPopup(loginRequest)
        .then(response => {
          if (response && response.account) {
            instance.setActiveAccount(response.account);
          }
          return response;
        })
        .catch((error) => {
          console.error('Login error:', error);
        });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-20 bg-backdrop/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-[#FCFCF9] shadow-md p-10 rounded-lg max-w-sm w-full relative z-60">
        <h1 className="text-2xl font-bold mb-4">{t('authPopup.welcome')}</h1>
        <p className="mb-6">{t('authPopup.message')}</p>
        <button
          onClick={handleLogin}
          className="w-full min-h-[48px] bg-[#FF5B24] text-white font-semibold rounded-lg py-3 flex items-center justify-center"
        >
          {t('authPopup.continueWithVipps')}
        </button>
        <button
          onClick={onClose}
          className="mt-4 w-full min-h-[48px] text-gray-700 hover:bg-gray-200 rounded-lg py-2"
        >
          {t('authPopup.cancel')}
        </button>
      </div>
    </div>
  );
};

export default AuthPopup;
